import {
  APIAdminOrganizationAttributes,
  APIOrganizationSettings,
  APIPublicOrganizationAttributes,
  APIUserDefaultAttributes,
  ArdoqId,
  LoginSetting,
  LoginSettingsResponse,
  UpdateUserManagementSettingsRequest,
} from '@ardoq/api-types';
import { isArdoqError } from '@ardoq/common-helpers';
import {
  fetchEntities,
  fetchEntity,
  fetchJson,
  fetchVoid,
  headers,
  Method,
  toRequestOptions,
} from './api';
import { ApiResponse } from './types';

const fetchOrganizationLogo = (
  organizationId: ArdoqId
): ApiResponse<{ logo: string }> => {
  const request = new Request(`/api/organization/${organizationId}/logo`, {
    headers,
  });
  return fetchJson<{ logo: string }>(request);
};

const fetchUsers = async (
  orgId: ArdoqId
): ApiResponse<APIUserDefaultAttributes[]> => {
  const request = new Request(`/api/organization/${orgId}/users`, { headers });
  return fetchJson<APIUserDefaultAttributes[]>(request);
};

const fetchUserById = async (
  userId: string,
  organizationId: string | null
): ApiResponse<APIUserDefaultAttributes> => {
  const users = organizationId ? await fetchUsers(organizationId) : [];

  if (isArdoqError(users)) {
    return users;
  }

  const user = users.find(({ _id }) => _id === userId);
  if (!user) {
    throw new Error('User not found');
  }
  return user;
};

// TODO[ARD-24008]: Consent should be updated as well in PUT /api/organization
const updateOrganization = async (
  attributes: APIAdminOrganizationAttributes
): ApiResponse<APIAdminOrganizationAttributes> => {
  const organization = await fetchJson<APIAdminOrganizationAttributes>(
    new Request(
      `/api/organization/${attributes._id}`,
      toRequestOptions(Method.PUT, attributes)
    )
  );

  if (isArdoqError(organization)) {
    return organization;
  }

  const consent = await fetchVoid(
    new Request(
      '/api/organization/consent/viewdata',
      toRequestOptions(Method.PUT, {
        hasConsentedToViewData: attributes.hasConsentedToViewData ?? false,
      })
    )
  );

  if (isArdoqError(consent)) {
    return consent;
  }

  return organization;
};

const updateUserManagementSettings = async (
  payload: UpdateUserManagementSettingsRequest
): ApiResponse<void> => {
  return fetchVoid(
    new Request(
      '/api/organization/settings/user-management',
      toRequestOptions(Method.PUT, payload)
    )
  );
};

const fetchLoginOptions = async (): ApiResponse<LoginSettingsResponse> => {
  const request = new Request(`/api/organization/settings/login`, { headers });
  return fetchJson<LoginSettingsResponse>(request);
};

const updateLoginOptionName = async (
  id: string,
  name: string
): ApiResponse<LoginSetting> => {
  return fetchJson(
    new Request(
      `/api/organization/settings/login/${id}`,
      toRequestOptions(Method.PUT, {
        loginOptionName: name,
      })
    )
  );
};

export const resolvePublicOrgLogo = (
  settings: APIOrganizationSettings
): string | undefined =>
  settings.brandSettings?.logoSettings?.publicLogo ?? undefined;

export const organizationApi = {
  fetchOrganizationLogo,
  fetchUsers,
  fetch: fetchEntity<
    APIAdminOrganizationAttributes | APIPublicOrganizationAttributes
  >('organization'),
  fetchAll: fetchEntities<APIAdminOrganizationAttributes>('organization'),
  updateOrganization,
  updateUserManagementSettings,
  fetchUserById,
  fetchLoginOptions,
  updateLoginOptionName,
};
